import { type FC, useId, useState } from "react";
import Link from "next/link";
import { Mail } from "lucide-react";
import { validateEmail } from "@/utils/constants";
import SweepstakesButton from "../SweepstakesButton";
import SweepstakesField from "../SweepstakesField";
import { Body, LineBreaks, Subhead } from "../SweepstakesText";
import { EMAIL_PRIVACY, type PopupCopy } from "../popupContent";

type Props = {
  copy: PopupCopy["email"];
  onEmail: (email: string) => void;
};

const EmailStep: FC<Props> = ({ copy, onEmail }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const errorId = useId();

  const submit = () => {
    const trimmed = email.trim();
    if (!validateEmail(trimmed)) {
      setError("Please enter a valid email.");
      return;
    }

    onEmail(trimmed);
  };

  return (
    <div className="flex w-full flex-col items-center gap-5 text-center md:gap-6 xl:gap-8 2xl:gap-10">
      <div className="flex w-full flex-col items-center gap-2">
        <Subhead>{copy.subhead}</Subhead>
        <Body>
          <LineBreaks value={copy.body} />
        </Body>
      </div>
      <div className="flex w-full flex-col gap-2">
        <SweepstakesField
          icon={<Mail className="size-6" />}
          label="Email address"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="Email address"
          value={email}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          onChange={(value) => {
            setEmail(value);
            if (error) setError("");
          }}
          onEnter={submit}
        />
        {error && (
          <p
            id={errorId}
            role="alert"
            /* The redesign's type ramp, but the error keeps its own colour: it sat
               on `legacy-error` before and lost it in the rework, which left a
               validation failure looking exactly like the caption beneath it. */
            className="font-ppMori text-[12px] leading-[16px] text-legacy-error md:text-[13px] md:leading-[18px] 2xl:text-[14px] 2xl:leading-[20px]"
          >
            {error}
          </p>
        )}
      </div>
      <SweepstakesButton onClick={submit}>{copy.cta}</SweepstakesButton>
      <p className="font-ppMori text-[12px] leading-[16px] text-[#1f2022] md:text-[13px] md:leading-[18px] 2xl:text-[14px] 2xl:leading-[20px]">
        {EMAIL_PRIVACY.lead}{" "}
        <Link href="/privacy-policy" className="underline">
          {EMAIL_PRIVACY.link}
        </Link>
      </p>
    </div>
  );
};

export default EmailStep;
